import { useEffect, useState, type ReactNode } from 'react';
import { BookOpenCheck, CheckCircle2, Clock3, ListChecks } from 'lucide-react';

type StatIconName = 'flows' | 'required' | 'quizzes' | 'time';

const iconMap: Record<StatIconName, ReactNode> = {
  flows: <ListChecks className="h-5 w-5" />,
  required: <BookOpenCheck className="h-5 w-5" />,
  quizzes: <CheckCircle2 className="h-5 w-5" />,
  time: <Clock3 className="h-5 w-5" />,
};

interface AchievementStatsProps {
  flowCount: number;
  completedFlowCount: number;
  chapterCount: number;
  completedChapterCount: number;
  quizCount: number;
  passedQuizCount: number;
  activeVisibleTimeSeconds: number;
}

const formatDuration = (seconds: number) => {
  const safeSeconds = Math.max(0, Math.round(seconds || 0));
  const hours = safeSeconds / 3600;
  if (hours >= 1) {
    const roundedHours = hours >= 10 ? Math.round(hours) : Math.round(hours * 10) / 10;
    return {
      value: String(roundedHours),
      sub: 'hours',
    };
  }

  const minutes = Math.max(1, Math.round(safeSeconds / 60));
  return {
    value: String(minutes),
    sub: 'mins',
  };
};

export function AchievementStats({
  flowCount,
  completedFlowCount,
  chapterCount,
  completedChapterCount,
  quizCount,
  passedQuizCount,
  activeVisibleTimeSeconds,
}: AchievementStatsProps) {
  const [show, setShow] = useState(false);
  const duration = formatDuration(activeVisibleTimeSeconds);
  const stats = [
    { label: 'Lessons completed', value: String(completedFlowCount), sub: `of ${flowCount}`, icon: 'flows' as StatIconName },
    ...(chapterCount > 0 ? [{ label: 'Chapters completed', value: String(completedChapterCount), sub: `of ${chapterCount}`, icon: 'required' as StatIconName }] : []),
    ...(quizCount > 0 ? [{ label: 'Quizzes passed', value: String(passedQuizCount), sub: `of ${quizCount}`, icon: 'quizzes' as StatIconName }] : []),
    { label: 'Active time', value: duration.value, sub: duration.sub, icon: 'time' as StatIconName },
  ];

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 500);
    return () => clearTimeout(t);
  }, []);

  return (
    <section aria-label="Achievement statistics" className="px-6 pb-16">
      <div className="mx-auto flex max-w-6xl flex-wrap justify-center gap-4">
        {stats.map((stat, i) => (
          <article
            key={stat.label}
            className={`group relative w-full max-w-[15.75rem] rounded-xl border border-border bg-secondary p-5 transition-all duration-500 ease-out hover:-translate-y-0.5 hover:shadow-lg md:p-6 ${
              show
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            }`}
            style={{ transitionDelay: `${i * 100}ms` }}
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                {iconMap[stat.icon]}
              </div>
              <div className="h-2 w-2 rounded-full bg-primary opacity-60 transition-opacity group-hover:opacity-100" aria-hidden="true" />
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">
                {stat.value}
              </span>
              <span className="text-sm text-muted-foreground">{stat.sub}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
          </article>
        ))}
      </div>
    </section>
  );
}
